import React from "react";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { Typography } from "@material-ui/core";
import { BoundsInInches, FieldModel } from "./types";
import TextIcon from "@material-ui/icons/TextFields";
import DateRangeIcon from "@material-ui/icons/DateRange";
import DialpadIcon from "@material-ui/icons/Dialpad";
import DehazeIcon from "@material-ui/icons/Dehaze";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import EditIcon from "@material-ui/icons/Edit";
import { typeOfDocumentsLabels } from "../../../TemplateBuilder/utils/typeOfDocuments";

export const fieldTypes = [
  {
    label: "Short Text",
    value: "shortText",
    icon: (
      <TextIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.text,
  },
  {
    label: "Date",
    value: "date",
    icon: (
      <DateRangeIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.date,
  },
  {
    label: "Number",
    value: "number",
    icon: (
      <DialpadIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.text,
  },
  {
    label: "Select",
    value: "select",
    icon: (
      <DehazeIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.select,
  },
  {
    label: "Checkbox",
    value: "checkbox",
    icon: (
      <CheckBoxIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.checkbox,
  },
  {
    label: "Radio",
    value: "radio",
    icon: (
      <RadioButtonCheckedIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.radio,
  },
  {
    label: "Signature",
    value: "signature",
    icon: (
      <BorderColorIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.signature,
  },
  {
    label: "Initials",
    value: "initials",
    icon: (
      <EditIcon
        style={{ color: "rgba(0, 106, 198, 1)" }}
        width={10}
        height={10}
        fontSize="small"
      />
    ),
    type: typeOfDocumentsLabels.initials,
  },
];

interface FieldValue {
  id: string;
  value: string;
  fieldType: string;
}

export function createFieldsPlugin(
  fields: FieldModel[],
  onSelect: (id: string) => void,
  selectedId: string | null,
  fieldValues: FieldValue[] = [],
  activeFieldId: string | null = null
) {
  console.log("Plugin fieldValues:", fieldValues); // Debug log
  console.log("Plugin fields:", fields); // Debug log

  const formatDateForDisplay = (value: string): string => {
    if (!value) return "";

    // 1) Explicit YYYY-MM-DD or YYYY/MM/DD (with optional time)
    const ymdMatch = value.match(/^(\d{4})[-/](\d{2})[-/](\d{2})(?:T.*)?$/);
    if (ymdMatch) {
      const [, y, m, d] = ymdMatch;
      return `${m}/${d}/${y}`;
    }

    // 2) Already in MM/DD/YYYY → leave as-is
    const mdyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (mdyMatch) {
      return value;
    }

    // 3) Fallback: try Date parsing but only as last resort
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      const year = parsed.getFullYear();
      return `${month}/${day}/${year}`;
    }

    // 4) If format is unknown, return as is
    return value;
  };

  return {
    renderPageLayer(props: any) {
      const { pageIndex, width: pageWidth, height: pageHeight } = props;

      // Calculate pixels per inch based on the actual page width (8.5 inches)
      const ppi = pageWidth / 8.5; // px por pulgada (escala actual)

      const pageFields = fields.filter(
        (f) => f.fieldPosition?.page === pageIndex + 1
      );

      return (
        <div
          style={{
            position: "relative",
            width: pageWidth,
            height: pageHeight,
          }}
        >
          {pageFields.map((f) => {
            const { x, y, width, height } = f.fieldPosition || {
              x: 0,
              y: 0,
              width: 0,
              height: 0,
            };

            // Convert inches to pixels
            const left = x * ppi;
            const top = y * ppi;
            const w = width * ppi;
            const h = height * ppi;

            const resolvedTemplateType = (f as { type?: string; fieldType?: string }).type
              || (f as { fieldType?: string }).fieldType;
            const iconField = fieldTypes.find(
              (ft) => ft.value === resolvedTemplateType
            )?.icon;

            // Find the current value for this field
            const currentValue =
              fieldValues.find((v) => String(v.id) === String(f.id))?.value || "";
            const fieldType =
              fieldValues.find((v) => String(v.id) === String(f.id))?.fieldType
              || resolvedTemplateType
              || "shortText";

            const isSelected = f.id === selectedId;
            const isActive = f.id === activeFieldId;
            const hasValue = !!currentValue;

            const getFieldStyles = () => {
              if (isActive) {
                return {
                  outline: '3px solid #2196F3',
                  boxShadow: '0 0 12px rgba(33, 150, 243, 0.6)',
                  background: hasValue ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.2)',
                  zIndex: 1002,
                };
              } else if (isSelected) {
                return {
                  outline: '2px solid #ff9800',
                  background: hasValue ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 152, 0, 0.5)',
                  zIndex: 1001,
                };
              } else {
                return {
                  outline: '1px solid #ff9800',
                  background: hasValue ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 152, 0, 0.3)',
                  zIndex: 1000,
                };
              }
            };

            const fieldStyles = getFieldStyles();

            // Render field value based on type
            const renderFieldValue = () => {
              if (!currentValue) return null;

              switch (fieldType) {
                case "checkbox":
                  return currentValue === "true" ? (
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#0076FF",
                        border: "2px solid #0076FF",
                        borderRadius: "2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    </div>
                  ) : null;
                case "signature":
                  return (
                    <img
                      src={currentValue}
                      alt="Signature"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  );
                case "date": {
                  const formattedDate = formatDateForDisplay(currentValue);
                  return (
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#000",
                        fontWeight: "500",
                        textAlign: "center",
                        wordBreak: "break-word",
                        lineHeight: "1.2",
                        padding: "2px",
                      }}
                    >
                      {formattedDate}
                    </div>
                  );
                }
                case "shortText":
                case "text":
                case "number":
                case "select":
                case "initials":
                default:
                  return (
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#000",
                        fontWeight: "500",
                        textAlign: "center",
                        wordBreak: "break-word",
                        lineHeight: "1.2",
                        padding: "2px",
                      }}
                    >
                      {currentValue}
                    </div>
                  );
              }
            };

            return (
              <div
                key={f.id}
                data-field-id={f.id}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width: w,
                  height: h,
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: 'all 0.2s ease-in-out',
                  ...fieldStyles,
                }}
              >
                {/* Field value overlay */}
                {currentValue && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1001,
                      pointerEvents: "none",
                    }}
                  >
                    {renderFieldValue()}
                  </div>
                )}

                {/* Field icon and title (only when no value) */}
                {!currentValue && (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color:
                        f.id === selectedId ? "white" : "rgba(0, 106, 198, 1)",
                      background:
                        f.id === selectedId
                          ? "rgba(255, 152, 0, 0.8)"
                          : "transparent",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(f.id);
                    }}
                    title={f.title}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "8px",
                      }}
                    >
                      {iconField} {f.title}
                    </span>
                  </div>
                )}

                {/* Clickable area for selection when there's a value */}
                {currentValue && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      cursor: "pointer",
                      zIndex: 1002,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(f.id);
                    }}
                    title={`${f.title}: ${currentValue}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    },
  };
}
